/**
 * Utility functions for selection logic in Contact Search and Select modal
 */

import { MembershipTypeIds, SelectableMembershipTypeIds } from 'modules/Constants'

/**
 * Name type priority for display order and default selection
 * Main Writing > Pseudonym > Correspondence > others
 */
export const NAME_TYPE_PRIORITY = { 2: 0, 3: 1, 4: 2 }

/**
 * Check if a name is selectable based on business rules
 * Uses typeId from server:
 * - TypeId 2 (Main Writing) - Always selectable
 * - TypeId 3 (Pseudonym) - Always selectable
 * - TypeId 4 (Correspondence) - Only selectable when it's the only name
 * - TypeId 1 (Maiden Name) - Not selectable
 * @param {ContactName} name - The name to check
 * @param {ContactName[]} allNames - All names for the contact
 * @returns {boolean} - True if the name is selectable
 */
export const isNameSelectable = (name, allNames) => {
    if (!name) return false

    // Main Writing (2) and Pseudonym (3) are always selectable
    if (name.typeId === 2 || name.typeId === 3) {
        return true
    }

    // Correspondence (4) is only selectable when it's the only name
    if (name.typeId === 4) {
        return allNames.length === 1
    }

    // Maiden Name (1) and others are not selectable
    return false
}

/**
 * Check if a membership is selectable based on business rules
 * - Only Ordinary, Reciprocal, and Payee are selectable (Group A)
 * - All other membership types are NOT selectable
 * @param {Membership} membership - The membership to check
 * @returns {boolean} - True if the membership is selectable
 */
export const isMembershipSelectable = (membership) => {
    if (!membership) return false

    return SelectableMembershipTypeIds.includes(membership.membershipTypeId)
}

/**
 * Get the default name to select for a contact
 * Priority: Main Writing (2) > Pseudonym (3) > Correspondence (4) only if it's the only name
 * @param {ContactName[]} names - Array of contact names
 * @returns {ContactName|null} - The default name to select
 */
export const getDefaultName = (names) => {
    if (!names || names.length === 0) return null

    const selectable = names
        .filter(n => isNameSelectable(n, names))
        .sort((a, b) => (NAME_TYPE_PRIORITY[a.typeId] ?? 99) - (NAME_TYPE_PRIORITY[b.typeId] ?? 99))

    return selectable[0] || null
}

/**
 * Get the default membership to select for a contact
 * Priority: Ordinary > Reciprocal > Payee
 * @param {Membership[]} memberships - Array of memberships
 * @returns {Membership|null} - The default membership to select
 */
export const getDefaultMembership = (memberships) => {
    if (!memberships || memberships.length === 0) return null

    // First priority: Ordinary
    const ordinary = memberships.find(m => m.membershipTypeId === MembershipTypeIds.Ordinary)
    if (ordinary) return ordinary

    // Second priority: Reciprocal
    const reciprocal = memberships.find(m => m.membershipTypeId === MembershipTypeIds.Reciprocal)
    if (reciprocal) return reciprocal

    // Third priority: Payee
    const payee = memberships.find(m => m.membershipTypeId === MembershipTypeIds.Payee)
    if (payee) return payee

    // No selectable membership found
    return null
}

/**
 * Check if the contact has no selectable memberships (no Ordinary, Reciprocal, or Payee)
 * @param {Membership[]} memberships - Array of memberships
 * @returns {boolean} - True if no memberships are selectable
 */
export const hasOnlySuccessorMemberships = (memberships) => {
    if (!memberships || memberships.length === 0) return false
    // Check if none of the memberships are selectable (Group A)
    return !memberships.some(m => SelectableMembershipTypeIds.includes(m.membershipTypeId))
}

/**
 * Check if the contact has any selectable names
 * @param {ContactName[]} names - Array of contact names
 * @returns {boolean} - True if at least one name is selectable
 */
export const hasSelectableNames = (names) => {
    if (!names || names.length === 0) return false
    return names.some(name => isNameSelectable(name, names))
}

/**
 * Check if the contact has any selectable memberships
 * @param {Membership[]} memberships - Array of memberships
 * @returns {boolean} - True if at least one membership is selectable
 */
export const hasSelectableMemberships = (memberships) => {
    if (!memberships || memberships.length === 0) return true // No memberships = potential member, OK
    return memberships.some(m => isMembershipSelectable(m))
}
